/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { forwardRef, Ref } from 'react';
import { Spinner, SPINNER_TYPE_EXTRA_SMALL } from '../Spinner';
import { Tooltip } from '../Tooltip';
import { buttonDefaults, ButtonProps } from './Button.props';
import { BUTTON_ICON_LEFT, BUTTON_ICON_RIGHT } from './types/ButtonIcon.types';

/**
 * SDS Button component.
 */
export const Button = forwardRef((props: ButtonProps, ref: Ref<HTMLButtonElement>): JSX.Element => {
  const {
    classes,
    customContent,
    dataId,
    format,
    icon,
    iconPosition,
    iconColour,
    isDisabled,
    isLite,
    isLoading,
    isRound,
    isRoundAndIconOnly,
    isWide,
    label,
    onClick,
    text,
    theme,
    tooltip,
    type,
    ...rest
  } = {
    ...buttonDefaults,
    ...props,
  };
  const buttonClass = clsx(`sds-button`, `button--${theme}`, `button--${format}`, classes, {
    [`button--icon`]: icon,
    [`button--icon-round`]: isRoundAndIconOnly,
    [`button--lite`]: isLite,
    [`button--loading`]: isLoading,
    [`button--round`]: isRound,
    [`button--wide`]: isWide,
  });

  const iconContent = icon && (
    <span className={clsx(`sds-button-icon`, iconColour && `svg--${iconColour}`)}>{icon}</span>
  );

  const buttonContent = (
    <>
      {customContent ? (
        <div className="button--custom">{customContent}</div>
      ) : (
        <div className="button--content">
          {iconPosition === BUTTON_ICON_LEFT && iconContent}
          {text && <span className="sds-button-label">{text}</span>}
          {iconPosition === BUTTON_ICON_RIGHT && iconContent}
        </div>
      )}
    </>
  );

  if (isLoading) {
    return (
      <button className={buttonClass}>
        <div className="button--spinner-container">
          <Spinner type={SPINNER_TYPE_EXTRA_SMALL} />
        </div>
        {buttonContent}
      </button>
    );
  }
  const button = (
    <button
      aria-label={label}
      className={buttonClass}
      data-id={dataId}
      disabled={isDisabled}
      onClick={(event): void => {
        event.preventDefault();
        onClick && onClick(event);
      }}
      ref={ref}
      tabIndex={0}
      type={type}
      {...rest}
    >
      {buttonContent}
    </button>
  );

  if (tooltip) {
    return <Tooltip {...tooltip}>{button}</Tooltip>;
  }
  return button;
});
